import { useEffect, useRef, useState, type ReactNode } from 'react';
import classNames from 'classnames';
import type { UiInputBindingId } from '@sorrem/shared';
import { TextTabs } from '@sorrem-client/ui/components';
import { formatBindingLabel, useInputBindingPress, useInputBindings } from '@sorrem-client/ui/hooks';
import styles from './PartyTabs.module.scss';

/** The Chat tab's view, and what tells it a message came in. */
export interface PartyChatTab {
    readonly content: ReactNode;
    /** The newest message's id: a change while another tab shows flashes Chat. */
    readonly latestMessageId: string | null;
}

/** The run's Log tab (#273): the combat log, shown or hidden as the account last left it. */
export interface PartyLogTab {
    readonly content: ReactNode;
    readonly isOpen: boolean;
    readonly onOpenChange: (open: boolean) => void;
}

type PartyTabId = 'party' | 'chat' | 'log';

interface PartyTabsProps {
    /** The Party tab's view: the party panel; null on a solo run, with nobody to list. */
    readonly party: ReactNode;
    /** The Chat tab; null where there's nobody to talk to. With no chat and no log, the party view shows alone with no tabs. */
    readonly chat: PartyChatTab | null;
    /** The Log tab, in a run; null or left out at the gathering. */
    readonly log?: PartyLogTab | null;
    /** Something on the Party tab needs the player (they haven't readied up, a member fell): flashes it while hidden. */
    readonly isPartyAttentionNeeded?: boolean;
}

/**
 * The party's HUD area at the gathering and in the run: the Party, Chat and
 * (in a run) Log views, one at a time, over a strip of plain text tabs along
 * the bottom, each with its key (show-party, show-chat, toggle-combat-log).
 * The log's key shows and hides it; with no party view (a solo run), hiding
 * it leaves only the strip. A tab that needs attention while another is
 * showing glows. The Chat and Log views sit on a soft gradient so their text
 * reads over any scene.
 */
export function PartyTabs({ party, chat, log = null, isPartyAttentionNeeded = false }: PartyTabsProps): JSX.Element {
    const [activeTab, setActiveTab] = useState<Exclude<PartyTabId, 'log'>>('party');
    const { bindings } = useInputBindings();
    const [hasUnreadChat, setHasUnreadChat] = useState(false);
    const latestMessageId = chat?.latestMessageId ?? null;
    // Messages already there when the tabs appear (such as on entering the run) count as read.
    const seenMessageIdRef = useRef(latestMessageId);
    const hasParty = party !== null;
    // The log takes over while open; otherwise Party or Chat, as last picked.
    const showing: PartyTabId | null = log?.isOpen ? 'log' : hasParty ? activeTab : null;

    useEffect(() => {
        if (latestMessageId === seenMessageIdRef.current) {
            return;
        }
        seenMessageIdRef.current = latestMessageId;
        if (showing !== 'chat') {
            setHasUnreadChat(true);
        }
    }, [showing, latestMessageId]);

    const selectTab = (tab: PartyTabId): void => {
        if (tab === 'log') {
            // Clicking the showing log closes it when there's nothing else to show.
            log?.onOpenChange(!(log.isOpen && !hasParty));
            return;
        }
        setActiveTab(tab);
        log?.onOpenChange(false);
        if (tab === 'chat') {
            setHasUnreadChat(false);
        }
    };
    const hasChat = chat !== null;
    useInputBindingPress('show-party', hasChat ? (): void => selectTab('party') : undefined);
    useInputBindingPress('show-chat', hasChat ? (): void => selectTab('chat') : undefined);
    useInputBindingPress('toggle-combat-log', log !== null ? (): void => log.onOpenChange(!log.isOpen) : undefined);

    if (chat === null && log === null) {
        return <div className={styles.partyTabs}>{party}</div>;
    }

    const tabs: readonly { id: PartyTabId; label: string; binding: UiInputBindingId; isFlashing: boolean }[] = [
        ...(hasParty ? [{ id: 'party' as const, label: 'Party', binding: 'show-party' as const, isFlashing: isPartyAttentionNeeded && showing !== 'party' }] : []),
        ...(hasChat ? [{ id: 'chat' as const, label: 'Chat', binding: 'show-chat' as const, isFlashing: hasUnreadChat && showing !== 'chat' }] : []),
        ...(log !== null ? [{ id: 'log' as const, label: 'Log', binding: 'toggle-combat-log' as const, isFlashing: false }] : []),
    ];
    const views: Record<PartyTabId, ReactNode> = { party, chat: chat?.content ?? null, log: log?.content ?? null };

    return (
        <div className={classNames(styles.partyTabs, (showing === 'chat' || showing === 'log') && styles.textShowing)}>
            {showing !== null && (
                <div role="tabpanel" id={`party-tab-${showing}`}>
                    {views[showing]}
                </div>
            )}
            <TextTabs
                tabs={tabs.map((tab) => ({
                    id: tab.id,
                    label: tab.label,
                    keyLabel: formatBindingLabel(bindings, tab.binding),
                    isFlashing: tab.isFlashing,
                    controls: `party-tab-${tab.id}`,
                }))}
                activeTab={showing}
                onTabChange={selectTab}
                ariaLabel={log !== null ? 'Party, chat and combat log' : 'Party and chat'}
            />
        </div>
    );
}
